import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Crown, Calendar, Clock, RefreshCw, Sparkles, AlertTriangle, Settings } from 'lucide-react';
import { pb } from '@/lib/pocketbase';
import { useAuth } from '@/hooks/useAuth';
import { format, differenceInDays, isPast, isFuture } from 'date-fns';
import { useNavigate } from 'react-router-dom';

interface UserSubscription {
  id: string;
  userId: string;
  planId: string;
  type: 'monthly' | 'yearly';
  status: 'active' | 'cancelled' | 'expired' | 'pending' | 'payment_failed';
  startsAt: string;
  expiresAt: string;
  created: string;
  orderId?: string;
  paymentStatus?: string;
  cardToken?: string;
  agreementId?: string;
  isRecurring?: boolean;
  lastRenewalDate?: string;
}

interface SubscriptionPlan {
  id: string;
  name: string;
  nameAr?: string;
  monthlyPrice: number;
  yearlyPrice: number;
}

export function SubscriptionSection() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [plan, setPlan] = useState<SubscriptionPlan | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSubscription = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      try {
        // Fetch user's active subscription
        const result = await pb.collection('user_subscriptions').getList<UserSubscription>(1, 1, {
          filter: `userId = "${user.id}"`,
          sort: '-created',
        });

        if (result.items.length > 0) {
          const sub = result.items[0];
          setSubscription(sub);

          // Fetch the plan details
          if (sub.planId) {
            try {
              const planData = await pb.collection('subscription_plans').getOne<SubscriptionPlan>(sub.planId);
              setPlan(planData);
            } catch (e) {
              console.warn('Could not fetch plan details:', e);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching subscription:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSubscription();
  }, [user?.id]);

  const getSubscriptionStatus = () => {
    if (!subscription) return null;

    const expiresAt = new Date(subscription.expiresAt);
    const now = new Date();
    const daysUntilExpiry = differenceInDays(expiresAt, now);

    if (subscription.status === 'pending' || subscription.paymentStatus === 'pending') {
      return { 
        label: 'Pending Payment', 
        labelAr: 'في انتظار الدفع',
        variant: 'warning' as const,
        icon: Clock 
      };
    }

    if (isPast(expiresAt) || subscription.status === 'expired') {
      return { 
        label: 'Expired', 
        labelAr: 'منتهي',
        variant: 'destructive' as const,
        icon: AlertTriangle 
      };
    }

    if (daysUntilExpiry <= 7) {
      return { 
        label: `Expiring in ${daysUntilExpiry} days`, 
        labelAr: `ينتهي خلال ${daysUntilExpiry} أيام`,
        variant: 'warning' as const,
        icon: AlertTriangle 
      };
    }

    return { 
      label: 'Active', 
      labelAr: 'نشط',
      variant: 'success' as const,
      icon: Sparkles 
    };
  };

  const handleRenew = () => {
    navigate('/subscription');
  };

  const handleSubscribe = () => {
    navigate('/subscription');
  };

  if (loading) {
    return (
      <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-10 w-32" />
        </CardContent>
      </Card>
    );
  }

  // No subscription - show CTA
  if (!subscription) {
    return (
      <Card className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20 border-amber-200 dark:border-amber-800">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-amber-800 dark:text-amber-200">
            <Crown className="w-5 h-5" />
            Unlock All Courses
            <span className="text-sm font-normal text-amber-600 dark:text-amber-400 mr-2">
              افتح جميع الدورات
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-amber-700 dark:text-amber-300">
            Subscribe to get unlimited access to all courses with a single monthly or yearly plan.
          </p>
          <p className="text-sm text-amber-600 dark:text-amber-400">
            اشترك للحصول على وصول غير محدود لجميع الدورات
          </p>
          <Button onClick={handleSubscribe} className="bg-amber-600 hover:bg-amber-700 text-white">
            <Crown className="w-4 h-4 mr-2" />
            View Subscription Plans
          </Button>
        </CardContent>
      </Card>
    );
  }

  const status = getSubscriptionStatus();
  const expiresAt = new Date(subscription.expiresAt);
  const startsAt = new Date(subscription.startsAt);
  const isExpired = isPast(expiresAt) || subscription.status === 'expired';
  const isExpiringSoon = differenceInDays(expiresAt, new Date()) <= 7 && !isExpired;

  return (
    <Card className={`border-2 ${
      isExpired 
        ? 'bg-gradient-to-br from-red-50 to-red-100 dark:from-red-950/20 dark:to-red-900/20 border-red-200 dark:border-red-800' 
        : isExpiringSoon
        ? 'bg-gradient-to-br from-yellow-50 to-amber-100 dark:from-yellow-950/20 dark:to-amber-900/20 border-yellow-200 dark:border-yellow-800'
        : 'bg-gradient-to-br from-green-50 to-emerald-100 dark:from-green-950/20 dark:to-emerald-900/20 border-green-200 dark:border-green-800'
    }`}>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2">
            <Crown className={`w-5 h-5 ${isExpired ? 'text-red-600' : 'text-green-600'}`} />
            {subscription.type === 'yearly' ? 'Yearly Subscription' : 'Monthly Subscription'}
            <span className="text-sm font-normal text-muted-foreground mr-2">
              {subscription.type === 'yearly' ? 'اشتراك سنوي' : 'اشتراك شهري'}
            </span>
          </CardTitle>
          {status && (
            <Badge 
              variant={status.variant === 'success' ? 'default' : status.variant === 'warning' ? 'secondary' : 'destructive'}
              className={
                status.variant === 'success' 
                  ? 'bg-green-500 text-white' 
                  : status.variant === 'warning' 
                  ? 'bg-yellow-500 text-white'
                  : ''
              }
            >
              <status.icon className="w-3 h-3 mr-1" />
              {status.label}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Plan Name */}
        {plan && (
          <div className="flex items-center gap-2 text-sm">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="font-medium">{plan.name}</span>
            {plan.nameAr && <span className="text-muted-foreground">({plan.nameAr})</span>}
          </div>
        )}

        {/* Dates */}
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-start gap-2">
            <Calendar className="w-4 h-4 text-muted-foreground mt-0.5" />
            <div>
              <p className="text-xs text-muted-foreground">Started</p>
              <p className="font-medium">{format(startsAt, 'MMM d, yyyy')}</p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <Clock className="w-4 h-4 text-muted-foreground mt-0.5" />
            <div>
              <p className="text-xs text-muted-foreground">
                {isExpired ? 'Expired' : 'Expires'}
              </p>
              <p className={`font-medium ${isExpired ? 'text-red-600' : isExpiringSoon ? 'text-yellow-600' : ''}`}>
                {format(expiresAt, 'MMM d, yyyy')}
              </p>
            </div>
          </div>
        </div>

        {/* Days remaining */}
        {!isExpired && (
          <div className="bg-white/50 dark:bg-black/20 rounded-lg p-3">
            <p className="text-sm text-center">
              <span className="font-bold text-2xl text-primary">
                {differenceInDays(expiresAt, new Date())}
              </span>
              <span className="text-muted-foreground ml-2">days remaining</span>
            </p>
          </div>
        )}

        {/* Renew Button */}
        {(isExpired || isExpiringSoon) && (
          <Button 
            onClick={handleRenew} 
            className={`w-full ${isExpired ? 'bg-red-600 hover:bg-red-700' : 'bg-yellow-600 hover:bg-yellow-700'}`}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            {isExpired ? 'Renew Subscription' : 'Renew Now & Save'}
          </Button>
        )}

        {/* Active subscription message */}
        {!isExpired && !isExpiringSoon && (
          <div className="space-y-2">
            <p className="text-sm text-center text-green-700 dark:text-green-300">
              You have full access to all courses
              <span className="block text-xs mt-1">لديك وصول كامل لجميع الدورات</span>
            </p>
            {subscription.cardToken && subscription.isRecurring && (
              <p className="text-xs text-center text-muted-foreground flex items-center justify-center gap-1">
                <RefreshCw className="w-3 h-3" />
                Auto-renewal enabled • التجديد التلقائي مفعل
              </p>
            )}
          </div>
        )}

        {/* Manage Subscription Button */}
        <Button 
          variant="outline" 
          onClick={() => navigate('/manage-subscription')} 
          className="w-full mt-4"
        >
          <Settings className="w-4 h-4 mr-2" />
          Manage Subscription / إدارة الاشتراك
        </Button>
      </CardContent>
    </Card>
  );
}
